import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { getDatabase, ref as dbRef, push } from "firebase/database";
import { getAuth } from "firebase/auth";
import { toPng } from 'html-to-image';
import axios from 'axios';
import { createCanvas, loadImage } from 'canvas';

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  width: 450px;
  max-width: 90%;
  max-height: 90%;
  overflow-y: auto;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
`;

const Button = styled.button`
  padding: 10px 20px;
  background-color: black;
  color: orange;
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background-color: #333;
  }
`;

const InputField = styled.input`
  margin: 5px 0;
  padding: 5px;
  width: 100%;
  border-radius: 4px;
  border: 1px solid #ccc;
`;

const InfoBalloon = styled.div`
  background-color: #ffeb3b;
  color: #333;
  padding: 15px;
  border-left: 5px solid #fbc02d;
  border-radius: 5px;
  margin-bottom: 20px;
  font-size: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const CharacterCounter = styled.span`
  font-size: 0.8em;
  color: #999;
  margin-left: 10px;
`;

const PrintingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.75);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 1100;
  color: white;
  font-size: 24px;
  text-align: center;
`;

const LoaderIcon = styled.div`
  border: 4px solid #f3f3f3;
  border-top: 4px solid orange;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 2s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const NutritionalTablePrintScreen = styled.table`
  width: 105%;
  height: 100%;
  border-collapse: collapse;
  text-align: left;
  border: 2px solid var(--color-gray-300);

  th {
    font-weight: bold;
    font-size: 1.05rem;
    border: 1px solid var(--color-gray-300);
  }

  .center {
    text-align: center;
    font-weight: bold;
    font-size: 1.15rem;
    border: 1px solid var(--color-gray-300);
  }

  .sub-header {
    font-size: 1rem;
    text-align: center;
    font-weight: normal;
    margin-bottom: 5px;
    border: 1px solid var(--color-gray-300);
  }

  td {
    padding: 5px;
    border: 1px solid var(--color-gray-300);
  }

  .footer-note {
    font-size: 0.9rem;
    text-align: left;
    padding-bottom: 0px;
    border: 1px solid var(--color-gray-300);
  }

  .footer-note-bold {
    font-size: 0.9rem;
    font-weight: bold;
    text-align: left;
    padding-bottom: 0px;
    border: 1px solid var(--color-gray-300);
  }

  .client-name {
    font-size: 1rem;
    font-weight: bold;
    text-align: left;
    padding-bottom: 0px;
    border: 1px solid var(--color-gray-300);
  }
`;

const PrintNutritionalLabelModal = ({ 
  isOpen, 
  onClose, 
  recipe, 
  nutritionalSummary, 
  initialData = {},
  onPrintSuccess 
}) => {
  const [step, setStep] = useState(1);
  const [printing, setPrinting] = useState(false);
  const [printStatus, setPrintStatus] = useState("");
  
  // Form data
  const [recipeName, setRecipeName] = useState("");
  const [clientName, setClientName] = useState("");
  const [fabricationDate, setFabricationDate] = useState("");
  const [expirationDate, setExpirationDate] = useState("");
  const [portionSize, setPortionSize] = useState(300);
  const [allergensText, setAllergensText] = useState("NÃO CONTÉM GLÚTEN/ CONTÉM LACTOSE.<br>ALÉRGICOS: PODE CONTER TRAÇOS DE GLÚTEN.");
  const [editableIngredients, setEditableIngredients] = useState("");

  const DPI = 203;
  const LABEL_WIDTH = Math.round(80 / 25.4 * DPI);
  const LABEL_HEIGHT = Math.round(70 / 25.4 * DPI);

  const tableStyle = {
    width: '100%',
    tableLayout: 'auto',
  };

  const cellStyle = {
    wordWrap: 'break-word',
  };

  // Pré-preenchimento com dados iniciais
  useEffect(() => {
    if (isOpen) {
      setRecipeName(initialData.recipeName || recipe?.description?.slice(0, 50) || "");
      setClientName(initialData.clientName || "");
      setFabricationDate(initialData.fabricationDate || "");
      setExpirationDate(initialData.expirationDate || "");
      setPortionSize(initialData.portionSize || 300);
      setAllergensText(initialData.allergensText || "NÃO CONTÉM GLÚTEN/ CONTÉM LACTOSE.<br>ALÉRGICOS: PODE CONTER TRAÇOS DE GLÚTEN.");
      setEditableIngredients(initialData.ingredients || "");
      setStep(1);
      setPrinting(false);
      setPrintStatus("");
    }
  }, [isOpen, initialData, recipe]);

  const savePrintHistory = async (printData) => {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      console.error("Usuário não autenticado");
      return;
    }

    const db = getDatabase();
    const historyRef = dbRef(db, `printHistory/${user.uid}`);
    
    try {
      const historyData = {
        ...printData,
        timestamp: new Date().toISOString(),
        userId: user.uid
      };
      
      await push(historyRef, historyData);
      console.log("Dados salvos no histórico com sucesso");
    } catch (error) {
      console.error("Erro ao salvar no histórico:", error);
    }
  };

  const generateZPL = async () => {
    try {
      const tableElement = document.getElementById('nutritional-table-print');
      if (!tableElement) {
        console.error('Tabela nutricional não encontrada.');
        return;
      }

      const dataUrl = await toPng(tableElement);
      const response = await fetch(dataUrl);
      const blob = await response.blob();
      const image = await loadImage(URL.createObjectURL(blob));

      const canvasWidth = LABEL_WIDTH;
      const canvasHeight = LABEL_HEIGHT;
      const canvas = createCanvas(canvasWidth, canvasHeight);
      const ctx = canvas.getContext('2d');

      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);

      const x = (canvasWidth - image.height) / 2;
      const y = (canvasHeight - image.width) / 2;

      ctx.translate(x + image.height / 2, y + image.width / 2);
      ctx.rotate(-Math.PI / 2);
      ctx.drawImage(image, -image.width / 2, -image.height / 2);

      const rotatedBlob = await new Promise((resolve) => canvas.toBlob(resolve));

      const formData = new FormData();
      formData.append('file', rotatedBlob, 'nutritional-table.png');

      const labelaryResponse = await axios.post('https://api.labelary.com/v1/graphics', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return labelaryResponse.data;
    } catch (error) {
      console.error('Erro ao gerar o ZPL:', error);
    }
  };

  const handlePrintLabel = async () => {
    setPrinting(true);
    setPrintStatus("Imprimindo...");

    try {
      const zplCode = await generateZPL();
      if (!zplCode) {
        throw new Error("Falha ao gerar o ZPL.");
      }

      const printData = {
        recipeId: recipe?.id,
        recipeName,
        clientName,
        fabricationDate,
        expirationDate,
        portionSize,
        allergensText,
        ingredients: editableIngredients,
        nutritionalSummary
      };

      const isDevelopment = process.env.NODE_ENV === 'development' || !window.BrowserPrint;
      
      if (isDevelopment) {
        setPrintStatus("Simulando impressão (modo desenvolvimento)...");
        
        setTimeout(async () => {
          try {
            await savePrintHistory(printData);
            setPrintStatus("Impressão simulada com sucesso! ✅");
            setTimeout(() => {
              setPrinting(false);
              onClose();
              if (onPrintSuccess) onPrintSuccess();
            }, 2000);
          } catch (error) {
            console.error("Erro ao salvar no histórico:", error);
            setPrintStatus("Erro ao salvar no histórico");
            setTimeout(() => setPrinting(false), 2000);
          }
        }, 1500);
        
        return;
      }

      // Código para produção com impressora real
      window.BrowserPrint.getDefaultDevice("printer", (printer) => {
        if (printer) {
          printer.send(
            zplCode,
            async (success) => {
              await savePrintHistory(printData);
              setPrintStatus("Impressão bem-sucedida!");
              setTimeout(() => {
                setPrinting(false);
                onClose();
                if (onPrintSuccess) onPrintSuccess();
              }, 2000);
            },
            (error) => {
              handlePrinterError(error);
            }
          );
        } else {
          window.BrowserPrint.getLocalDevices(
            (deviceList) => {
              const printer = deviceList.find((device) => device.deviceType === "printer");

              if (printer) {
                printer.send(
                  zplCode,
                  async (success) => {
                    await savePrintHistory(printData);
                    setPrintStatus("Impressão bem-sucedida!");
                    setTimeout(() => {
                      setPrinting(false);
                      onClose();
                      if (onPrintSuccess) onPrintSuccess();
                    }, 2000);
                  },
                  (error) => {
                    handlePrinterError(error);
                  }
                );
              } else {
                setPrintStatus("Nenhuma impressora Zebra conectada. Verifique a conexão.");
                setTimeout(() => setPrinting(false), 2000);
              }
            },
            (error) => {
              handlePrinterError({ message: "ERR_CONNECTION_REFUSED" });
            }
          );
        }
      }, (error) => {
        handlePrinterError({ message: "ERR_CONNECTION_REFUSED" });
      });
    } catch (error) {
      handlePrinterError(error);
    }
  };

  const handlePrinterError = (error) => {
    console.error("Erro de impressão: ", error);
    let userMessage = "Ocorreu um erro desconhecido.";

    if (error && error.message) {
      const errorMessage = error.message;

      if (errorMessage.includes("ERR_CONNECTION_REFUSED")) {
        userMessage = "Erro de conexão. Verifique se o software da Zebra está instalado e a impressora está conectada.";
      } else if (errorMessage.includes("paperOut")) {
        userMessage = "A impressora está sem papel.";
      } else if (errorMessage.includes("ribbonOut")) {
        userMessage = "A impressora está sem ribbon.";
      } else if (errorMessage.includes("headOpen")) {
        userMessage = "A cabeça de impressão está aberta. Feche-a para continuar.";
      } else if (errorMessage.includes("paused")) {
        userMessage = "A impressora está pausada. Verifique o painel.";
      }
    }

    setPrintStatus(userMessage);
    setTimeout(() => setPrinting(false), 3000);
  };

  const handleAdvance = () => {
    if (clientName && fabricationDate && expirationDate) {
      setStep(2);
    } else {
      alert("Todos os campos devem ser preenchidos.");
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const adjustedDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
    return adjustedDate.toLocaleDateString("pt-BR");
  };

  const formatNumberWithComma = (value) => {
    if (!value) return '0';
    return value.toString().replace('.', ',');
  };

  const referenceValues = {
    energy: 2000,
    proteins: 50,
    carbohydrates: 300,
    dietaryFiber: 25,
    totalFats: 70,
    addedSugars: 50,
    saturatedFats: 20,
    transFats: 0,
    sodium: 2400,
  };

  if (!isOpen) return null;

  return (
    <>
      <Modal>
        <ModalContent>
          {step === 1 ? (
            <div>
              <h3>Preencha os dados</h3>
              {initialData.recipeName && (
                <InfoBalloon style={{ backgroundColor: '#e8f5e8', color: '#2d5a2d' }}>
                  🔄 <strong>Reimpressão:</strong> Os dados foram pré-preenchidos do histórico. Você pode editá-los antes de imprimir.
                </InfoBalloon>
              )}
              <InfoBalloon>
                Esta funcionalidade foi desenvolvida e testada para impressoras da marca Zebra. A impressão só acontecerá
                se este sistema conseguir se comunicar com o software de impressão do fabricante, devidamente instalado no computador ao qual a impressora está conectada.
              </InfoBalloon>
              {(process.env.NODE_ENV === 'development' || !window.BrowserPrint) && (
                <InfoBalloon style={{ backgroundColor: '#fff3cd', color: '#856404', borderLeft: '5px solid #ffc107' }}>
                  🧪 <strong>Modo Desenvolvimento:</strong> A impressão será simulada para testes. O histórico será salvo normalmente.
                </InfoBalloon>
              )}
              <InputField
                type="text"
                value={recipeName}
                onChange={(e) => setRecipeName(e.target.value.slice(0, 50))}
                placeholder="Nome da Receita"
                maxLength={50}
              />
              <CharacterCounter>{50 - recipeName.length} caracteres restantes</CharacterCounter>
              <InputField
                type="text"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                placeholder="Nome do Cliente"
              />
              <label>Data de Fabricação</label>
              <InputField
                type="date"
                value={fabricationDate}
                onChange={(e) => setFabricationDate(e.target.value)}
              />
              <label>Data de Validade</label>
              <InputField
                type="date"
                value={expirationDate}
                onChange={(e) => setExpirationDate(e.target.value)}
              />
              <label>Tamanho da Porção (g)</label>
              <InputField
                type="number"
                value={portionSize}
                onChange={(e) => setPortionSize(e.target.value)}
              />
              <InfoBalloon style={{ marginTop: '4px', fontSize: '11px' }}>
                Este campo ajusta apenas a apresentação da porção na etiqueta impressa. 
                Não altera as quantidades dos ingredientes ou os cálculos nutricionais da receita.
              </InfoBalloon>
              <label>Ingredientes</label>
              <InputField
                type="text"
                value={editableIngredients}
                onChange={(e) => setEditableIngredients(e.target.value)}
              />
              <label>Texto dos Alérgenos</label>
              <InputField
                type="text"
                value={allergensText}
                onChange={(e) => setAllergensText(e.target.value)}
              />
              <ModalActions>
                <Button onClick={onClose}>Fechar</Button>
                <Button onClick={handleAdvance}>Avançar</Button>
              </ModalActions>
            </div>
          ) : (
            <div>
              <h2>Confirme os dados para impressão</h2>
              <NutritionalTablePrintScreen id="nutritional-table-print" style={tableStyle}>
                <thead>
                  <tr>
                    <th colSpan="4" className="center" style={cellStyle}>{recipeName || "Receita"}</th>
                  </tr>
                  <tr>
                    <th colSpan="4" className="sub-header" style={cellStyle}>Informação Nutricional/Porção de {portionSize}g (1 unidade)</th>
                  </tr>
                  <tr>
                    <th style={cellStyle}></th>
                    <th style={cellStyle}>100g</th>
                    <th style={cellStyle}>{portionSize}g</th>
                    <th style={cellStyle}>% VD*</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={cellStyle}>Valor energético (kcal)</td>
                    <td style={cellStyle}>{((nutritionalSummary.energy * 100 / portionSize) || 0).toFixed(1).replace('.', ',')}</td>
                    <td style={cellStyle}>{(nutritionalSummary.energy || 0).toFixed(1).replace('.', ',')}</td>
                    <td style={cellStyle}>{Math.round(((nutritionalSummary.energy || 0) / referenceValues.energy) * 100)}</td>
                  </tr>
                  <tr>
                    <td style={cellStyle}>Carboidratos (g)</td>
                    <td style={cellStyle}>{((nutritionalSummary.carbohydrates * 100 / portionSize) || 0).toFixed(1).replace('.', ',')}</td>
                    <td style={cellStyle}>{(nutritionalSummary.carbohydrates || 0).toFixed(1).replace('.', ',')}</td>
                    <td style={cellStyle}>{Math.round(((nutritionalSummary.carbohydrates || 0) / referenceValues.carbohydrates) * 100)}</td>
                  </tr>
                  <tr>
                    <td style={cellStyle}>Açúcares totais (g)</td>
                    <td style={cellStyle}>{((nutritionalSummary.totalSugars * 100 / portionSize) || 0).toFixed(1).replace('.', ',')}</td>
                    <td style={cellStyle}>{(nutritionalSummary.totalSugars || 0).toFixed(1).replace('.', ',')}</td>
                    <td style={cellStyle}>**</td>
                  </tr>
                  <tr>
                    <td style={cellStyle}>Açúcares adicionados (g)</td>
                    <td style={cellStyle}>{((nutritionalSummary.addedSugars * 100 / portionSize) || 0).toFixed(1).replace('.', ',')}</td>
                    <td style={cellStyle}>{(nutritionalSummary.addedSugars || 0).toFixed(1).replace('.', ',')}</td>
                    <td style={cellStyle}>{Math.round(((nutritionalSummary.addedSugars || 0) / referenceValues.addedSugars) * 100)}</td>
                  </tr>
                  <tr>
                    <td style={cellStyle}>Proteínas (g)</td>
                    <td style={cellStyle}>{((nutritionalSummary.proteins * 100 / portionSize) || 0).toFixed(1).replace('.', ',')}</td>
                    <td style={cellStyle}>{(nutritionalSummary.proteins || 0).toFixed(1).replace('.', ',')}</td>
                    <td style={cellStyle}>{Math.round(((nutritionalSummary.proteins || 0) / referenceValues.proteins) * 100)}</td>
                  </tr>
                  <tr>
                    <td style={cellStyle}>Gorduras totais (g)</td>
                    <td style={cellStyle}>{((nutritionalSummary.totalFats * 100 / portionSize) || 0).toFixed(1).replace('.', ',')}</td>
                    <td style={cellStyle}>{(nutritionalSummary.totalFats || 0).toFixed(1).replace('.', ',')}</td>
                    <td style={cellStyle}>{Math.round(((nutritionalSummary.totalFats || 0) / referenceValues.totalFats) * 100)}</td>
                  </tr>
                  <tr>
                    <td style={cellStyle}>Gorduras saturadas (g)</td>
                    <td style={cellStyle}>{((nutritionalSummary.saturatedFats * 100 / portionSize) || 0).toFixed(1).replace('.', ',')}</td>
                    <td style={cellStyle}>{(nutritionalSummary.saturatedFats || 0).toFixed(1).replace('.', ',')}</td>
                    <td style={cellStyle}>{Math.round(((nutritionalSummary.saturatedFats || 0) / referenceValues.saturatedFats) * 100)}</td>
                  </tr>
                  <tr>
                    <td style={cellStyle}>Gorduras trans (g)</td>
                    <td style={cellStyle}>{((nutritionalSummary.transFats * 100 / portionSize) || 0).toFixed(1).replace('.', ',')}</td>
                    <td style={cellStyle}>{(nutritionalSummary.transFats || 0).toFixed(1).replace('.', ',')}</td>
                    <td style={cellStyle}>**</td>
                  </tr>
                  <tr>
                    <td style={cellStyle}>Fibras alimentares (g)</td>
                    <td style={cellStyle}>{((nutritionalSummary.dietaryFiber * 100 / portionSize) || 0).toFixed(1).replace('.', ',')}</td>
                    <td style={cellStyle}>{(nutritionalSummary.dietaryFiber || 0).toFixed(1).replace('.', ',')}</td>
                    <td style={cellStyle}>{Math.round(((nutritionalSummary.dietaryFiber || 0) / referenceValues.dietaryFiber) * 100)}</td>
                  </tr>
                  <tr>
                    <td style={cellStyle}>Sódio (mg)</td>
                    <td style={cellStyle}>{Math.round(nutritionalSummary.sodium * 100 / portionSize)}</td>
                    <td style={cellStyle}>{Math.round(nutritionalSummary.sodium || 0)}</td>
                    <td style={cellStyle}>{Math.round(((nutritionalSummary.sodium || 0) / referenceValues.sodium) * 100)}</td>
                  </tr>
                  <tr>
                    <td style={cellStyle} colSpan="4" className="footer-note">*Percentual de valores diários fornecidos pela porção.</td>
                  </tr>
                  <tr>
                    <td style={cellStyle} colSpan="4" className="footer-note">*Valores diários de referência com base em uma dieta de 2000 kcal ou 8400 kj. Seus valores diários podem ser maiores ou menores dependendo de suas necessidades energéticas.</td>
                  </tr>
                  <tr>
                    <td style={cellStyle} colSpan="4" className="footer-note">Ingredientes: {editableIngredients}</td>
                  </tr>
                  <tr>
                    <td style={cellStyle} colSpan="4" className="footer-note-bold" dangerouslySetInnerHTML={{ __html: allergensText }}></td>
                  </tr>
                  <tr>
                    <td style={cellStyle} colSpan="4" className="footer-note-bold">FAB: {formatDate(fabricationDate)} &emsp; VAL: {formatDate(expirationDate)}</td>
                  </tr>
                  <tr>
                    <td style={cellStyle} colSpan="4" className="client-name">{clientName}</td>
                  </tr>
                </tbody>
              </NutritionalTablePrintScreen>
              <ModalActions>
                <Button onClick={() => setStep(1)}>Voltar</Button>
                <Button onClick={handlePrintLabel}>Confirmar e Imprimir</Button>
              </ModalActions>
            </div>
          )}
        </ModalContent>
      </Modal>

      {printing && (
        <PrintingOverlay>
          <LoaderIcon />
          <p>{printStatus}</p>
        </PrintingOverlay>
      )}
    </>
  );
};

export default PrintNutritionalLabelModal; 